import React, {Component} from "react";
import {GoogleLogo} from "../../logos/GoogleLogo";
import {FacebookLogo} from "../../logos/FacebookLogo";
import {FaIcon} from "../../FaIcon";
import {SheetLogo} from "../../logos/SheetLogo";
import {Switch} from "../../buttons/Swtich";
import {TiktokLogo} from "../../logos/TiktokLogo";
import {LinkedinLogo} from "../../logos/LinkedinLogo";
import {ShopifyLogo} from "../../logos/ShopifyLogo";
import {TiendaNubeLogo} from "../../logos/TiendaNubeLogo";
import {WooCommerceLogo} from "../../logos/WooCommerceLogo";
import {VtexLogo} from "../../logos/VtexLogo";
import {AnalyticsLogo} from "../../logos/AnalyticsLogo";
import {DV360Logo} from "../../logos/DV360Logo";
import {MercadoAdsLogo} from "../../logos/MercadoAdsLogo";
import {SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL, WOOCOMMERCE_CHANNEL, VTEX_CHANNEL, ANALYTICS_CHANNEL, CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_PAID_MEDIA, DV360_CHANNEL, MERCADO_ADS_CHANNEL} from "../../channels/channels";
import {CurrencyConversionInfo} from "../../elements/CurrencyConversionInfo";
import {ReconnectAdAccountModal} from "../../agency/connections/ReconnectAdAccountModal";
import {
    ERROR_KIND_ACCOUNT_DISABLED,
    isFixedInTheSpreadsheet,
    resolveErrorKind
} from "../../channels/adAccountErrorKinds";
import {withTranslation} from 'react-i18next';
import {UncoveredSpendChip} from "../../elements/UncoveredSpendChip";
import {hasUncoveredSpend, ruleLeakOf} from "../../elements/uncoveredSpend";

/**
 * Estados que puede mostrar UNA cuenta publicitaria en el tablero. Se muestra UNO solo, con
 * prioridad conexión > datos no actualizados > 0 impresiones (`_cellState`).
 *
 * Hasta 2026-07 la celda leía `generalAlerts`, que es de la LÍNEA entera y se reenviaba idéntico
 * a cada fila: una cuenta caída pintaba TODAS las cuentas en rojo y colapsaba "sin acceso" /
 * "sin impresiones" / "desactivada" en un mismo color. Ahora el backend manda `account_status`
 * POR FILA y cada cuenta muestra su estado real.
 */
const STATE_NONE = 'NONE';
const STATE_NO_CONNECTION = 'NO_CONNECTION';        // A — sin conexión con la cuenta
const STATE_ACCOUNT_DISABLED = 'ACCOUNT_DISABLED';  // B — desactivada en la plataforma (confirmado)
const STATE_DATA_STALE = 'DATA_STALE';              // C — sana, pero con datos no actualizados
const STATE_NO_IMPRESSIONS = 'NO_IMPRESSIONS';      // D — sana y sin impresiones REALES hoy
const STATE_LEGACY_ALERT = 'LEGACY_ALERT';          // compat: backend viejo, sin `account_status`

/**
 * El enum de `error_kind` y su resolver con fallback viven en `channels/adAccountErrorKinds.js`:
 * los comparten esta celda, el modal de reconexión y la fila del Listado v2. Cualquier valor
 * desconocido cae en GENERIC — el mensaje dice "no pudimos acceder", nunca "deshabilitada":
 * afirmar que una cuenta está deshabilitada sin confirmación de la plataforma es exactamente el
 * falso positivo que originó este fix.
 */

// Las fechas llegan formateadas del backend y pueden traer "/" — sin esto i18next las escapa
// a entidades HTML y se ven crudas (ver gotcha de interpolación en el CLAUDE.md del repo).
const NO_ESCAPE = {escapeValue: false};

class BusinessUnitAdAccountNameCellClass extends Component {

    constructor(props) {
        super(props);

        this.state = {
            showOnlyActive: true,
            showFilter: this.props.showFilter,
            showReconnectModal: false
        };

        this.handleOnlyActiveChange = this.handleOnlyActiveChange.bind(this);
        this.handleReconnectToggle = this.handleReconnectToggle.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.showFilter !== prevProps.showFilter) {
            this.setState({showFilter:this.props.showFilter});
        }
    }

    _getChannelClassName() {
        return ("bu-cell ad_account_row")
    }

    _renderTooltipIfNecessary() {
        switch (this._cellState()) {
            case STATE_NO_CONNECTION:
            case STATE_ACCOUNT_DISABLED:
                return this._renderNoConnectionTooltip();
            case STATE_DATA_STALE:
                return this._renderDataStaleTooltip();
            case STATE_NO_IMPRESSIONS:
            case STATE_LEGACY_ALERT:
                return this._renderSimpleAlertTooltip(this._alertMessage());
            default:
                return null;
        }
    }

    /**
     * Sin conexión / desactivada en la plataforma: por qué falló + desde cuándo + qué implica
     * para los datos + CTA de reconexión (mismo flujo que la ficha de cuentas del cliente).
     * NUNCA se muestra `error_found` crudo (es un mensaje interno legacy).
     */
    _renderNoConnectionTooltip() {
        const {t} = this.props;
        const connection = this._connection();
        const since = connection.no_access_since;

        return <div className="bu-tooltip -multiline">
            <FaIcon iconName="exclamation-triangle"/>
            <p className="tooltip-description">
                {t(`reconnect_modal.error_kind.${this._errorKind()}`)}<br/>
                {since
                    ? t('account_status.no_connection', {since: since, interpolation: NO_ESCAPE})
                    : t('account_status.no_connection_no_date')}<br/>
                {t('account_status.data_not_included')}
                {this._renderReconnectLinkIfPossible()}
            </p>
        </div>;
    }

    /**
     * Datos no actualizados con la conexión SANA: nunca rojo. Es el aviso que hubiese evitado
     * el ticket de Atómica (la cuenta mostraba números viejos sin decirlo en ningún lado).
     */
    _renderDataStaleTooltip() {
        const {t} = this.props;
        const lastUpdate = this._accountStatus().stale_since;

        return <div className="bu-tooltip -multiline">
            <FaIcon iconName="clock"/>
            <p className="tooltip-description">
                {t('account_status.stale_title')}
                {lastUpdate && <><br/>
                    {t('account_status.stale_detail', {last_update: lastUpdate, interpolation: NO_ESCAPE})}
                </>}
            </p>
        </div>;
    }

    _renderSimpleAlertTooltip(message) {
        if (!message)
            return null;

        return <div className="bu-tooltip">
            <FaIcon iconName="exclamation-triangle"/>
            <p className="tooltip-description">
                {message}
            </p>
        </div>;
    }

    _renderReconnectLinkIfPossible() {
        if (!this._canReconnect())
            return null;

        const label = this.props.t('reconnect_modal.reconnect');

        // <button> y no <span>: el CTA vive en un tooltip y tiene que ser operable con teclado
        // y anunciarse como acción a un lector de pantalla (Enter/Espacio salen gratis).
        // El reset va inline y NO como shorthand `font`: eso pisaría el `font-weight: 600` de
        // `.bu-tooltip .reconnect-link` (un estilo inline le gana a cualquier selector).
        // `border`/`background` ya los resetea el `button` global de `_buttons.scss`; se
        // repiten acá para que el botón no dependa de ese orden de imports.
        return <><br/>
            <button type="button"
                    className="reconnect-link"
                    onClick={this.handleReconnectToggle}
                    title={label}
                    style={{
                        border: 0,
                        background: 'none',
                        padding: 0,
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 'inherit'
                    }}>
                <FaIcon iconName="sync-alt"/> {label}
            </button>
        </>;
    }

    _renderReconnectModalIfNecessary() {
        if (!this.state.showReconnectModal)
            return null;

        const connection = this._connection();
        if (!connection)
            return null;

        return <ReconnectAdAccountModal
            isOpen={this.state.showReconnectModal}
            onToggle={this.handleReconnectToggle}
            adAccountData={{
                ad_account_id: connection.ad_account_pk,
                name: this.props.accountName,
                channel: this.props.channel,
                agency_channel_credential_label: connection.agency_channel_credential_label,
                error_found: connection.error_found,
                error_kind: this._errorKind(),
                // Quién ve la cuenta y desde cuándo (UD-20): el modal lo dice. Sólo vienen para la agencia.
                platform_user_name: connection.platform_user_name,
                connected_by_user_name: connection.connected_by_user_name,
                no_access_since_at: connection.no_access_since_at,
            }}
            onReconnected={() => window.location.reload()}
        />;
    }

    handleReconnectToggle() {
        this.setState({showReconnectModal: !this.state.showReconnectModal});
    }

    /**
     * Mensaje de los estados de una sola línea: 0 impresiones reales (D) y el fallback legacy
     * con `generalAlerts` (backend viejo durante el deploy).
     */
    _alertMessage() {
        const {t} = this.props;

        if (this._cellState() === STATE_NO_IMPRESSIONS)
            return t('account_status.no_impressions_today');

        const generalAlerts = this.props.generalAlerts;
        if (generalAlerts && generalAlerts.has_no_access_alert)
            return generalAlerts.has_no_access_alert;
        if (generalAlerts && this._isPaidMediaChannel() && generalAlerts.has_no_impression_alert)
            return t('account_status.no_impressions_today');
        return ''
    }

    render() {
        const uncoveredSpend = this._renderUncoveredSpendIfNecessary();
        return (
            <td>
                <div className={`${this._getChannelClassName()}${uncoveredSpend ? ' -has-uncovered' : ''}`}>
                    <div className="bu-name-with-tooltip">
                        <div className="bucc-name">
                            {this._ad_account_detailed_name()}
                        </div>
                        {/* Fuera de `.bucc-name` a propósito: ese contenedor tiene overflow
                            hidden y clipearía el tooltip. */}
                        {this._renderCurrencyIfConverted()}
                        {this._renderDataStaleIconIfNecessary()}
                        {this._renderTooltipIfNecessary()}
                    </div>
                    <div className="some-icons">
                        {this.renderAllCampaignsFilter()}
                        {this.renderOnlyWith30dImpressionsLabel()}
                        {/*{this.renderToggleDetails()}*/}
                    </div>
                    {uncoveredSpend}
                    {this._renderReconnectModalIfNecessary()}
                </div>
            </td>
        );
    }

    /**
     * Lo que la regla de ESTA cuenta deja afuera de la línea. Es de la agencia: el portal del cliente no lo ve.
     * - Con la fuga de su regla (`rule_leak` con datos): el mismo chip que la fila de la línea, con sólo esta
     *   cuenta.
     * - Con `rule_leak: null`: su regla no deja nada afuera, y no hay chip. La plata de la cuenta que no mira
     *   ninguna línea se ve en «Inversión sin control», a la que lleva la tarjeta de arriba de la tabla (UD-5).
     * - Sin la clave (backend viejo): el chip de siempre.
     */
    _renderUncoveredSpendIfNecessary() {
        if (this.props.viewMode === 'client')
            return null;

        const leak = ruleLeakOf(this.props.uncoveredSpend);
        if (!hasUncoveredSpend(leak))
            return null;

        return <UncoveredSpendChip uncoveredSpend={leak}
                                   accounts={[{
                                       id: this.props.buccId,
                                       channel: this.props.channel,
                                       account_id: this.props.accountId,
                                       name: this.props.accountName,
                                   }]}
                                   businessUnitId={this.props.businessUnitId}
                                   rowClassName="usp-row -account"/>;
    }

    renderOnlyWith30dImpressionsLabel() {
        if(!this.props.showOnlyActiveLabel)
            return

        return <span className="ml-2 mr-2  only_active">{this.props.t('dashboard.account_campaigns_with_impressions_30d')}</span>;
    }

    renderAllCampaignsFilter() {
        if(!this.state.showFilter)
            return null;

        return <div className="status-toggl" style={{fontSize:"0.85em"}}>
            <Switch key={this.props.buccId} onChange={this.handleOnlyActiveChange} checked={this.state.showOnlyActive}/>
            <span className="status-label">{this.props.t('dashboard.account_only_active_campaigns')}</span>
        </div>;
    }

    handleOnlyActiveChange(event) {
        const showOnlyActive = event.target.checked;

        this.setState({
            showOnlyActive: showOnlyActive
        });

        this.props.onOnlyActiveCampaignsChange(this.props.buccId, showOnlyActive);
    }

    // renderToggleDetails() {
    //     return <BusinessUnitShowAdAccountsOrCampaignsTooltip key="bu-show-ad-accounts-icon"
    //                                                          message={null}
    //                                                          onClick={this.props.onCampaignsDataToggle}
    //                                                          enabled={true}
    //                                                          isShowing={this.props.campaignsRowsExpanded}
    //     />
    // }

    _isPaidMediaChannel() {
        return CHANNEL_INFO_BY_CHANNEL[this.props.channel]?.channelType === CHANNEL_TYPE_PAID_MEDIA;
    }

    // estado de la celda

    /**
     * `account_status` puede llegar `undefined` (backend viejo mientras se deploya): en ese caso
     * la celda cae al comportamiento histórico con `generalAlerts` de la línea.
     */
    _accountStatus() {
        const accountStatus = this.props.accountStatus;
        if (accountStatus === undefined || accountStatus === null)
            return null;
        return accountStatus;
    }

    _connection() {
        const accountStatus = this._accountStatus();
        return (accountStatus && accountStatus.connection) || null;
    }

    _errorKind() {
        const connection = this._connection();
        return resolveErrorKind(connection && connection.error_kind);
    }

    _cellState() {
        const accountStatus = this._accountStatus();

        if (accountStatus === null)
            return this._legacyHasAlert() ? STATE_LEGACY_ALERT : STATE_NONE;

        if (this._connection() !== null)
            return this._errorKind() === ERROR_KIND_ACCOUNT_DISABLED ? STATE_ACCOUNT_DISABLED : STATE_NO_CONNECTION;

        if (accountStatus.data_stale)
            return STATE_DATA_STALE;

        // `no_impressions_today` en null es DESCONOCIDO: no se alerta ni a favor ni en contra.
        if (accountStatus.no_impressions_today === true && this._isPaidMediaChannel())
            return STATE_NO_IMPRESSIONS;

        return STATE_NONE;
    }

    _legacyHasAlert() {
        const generalAlerts = this.props.generalAlerts;
        if (!generalAlerts) return false;
        if (generalAlerts.has_no_access_alert) return true;
        return Boolean(this._isPaidMediaChannel() && generalAlerts.has_no_impression_alert);
    }

    /**
     * Rojo SOLO para lo crítico: sin conexión, desactivada confirmada, 0 impresiones reales.
     * Datos no actualizados (C) jamás pinta en rojo — si todo es rojo, nada es rojo.
     */
    _hasAlert() {
        const state = this._cellState();
        return state === STATE_NO_CONNECTION
            || state === STATE_ACCOUNT_DISABLED
            || state === STATE_NO_IMPRESSIONS
            || state === STATE_LEGACY_ALERT;
    }

    _canReconnect() {
        const connection = this._connection();
        if (!connection || !connection.ad_account_pk)
            return false;
        // En el portal del cliente no se ofrecen acciones de la agencia.
        if (this.props.viewMode === 'client')
            return false;
        // Un CSV con datos mal formados —o una planilla que ya no se puede leer— no se arregla
        // reconectando: se arregla en la planilla o en su propia conexión. El copy del kind ya dice
        // qué hacer; ofrecer "Reconectar" acá sería un botón muerto más.
        if (isFixedInTheSpreadsheet(this._errorKind()))
            return false;
        // Mismo criterio que la ficha de cuentas: sólo estos canales tienen flujo OAuth.
        return this._isPaidMediaChannel() || this.props.channel === ANALYTICS_CHANNEL;
    }

    // helpers

    // Fragment y no un array: un array de elementos exige `key` en cada hijo (React lo trata
    // como lista) y tiraba el warning "Each child in a list should have a unique key".
    _ad_account_detailed_name() {
        return <>
            {this._getPlatformLogo()}
            {this._getNameWithAlert()}
        </>;
    }

    /**
     * Moneda de la cuenta, SOLO cuando difiere de la de la línea de control (en ese caso sus
     * importes se muestran convertidos). Si coinciden no mostramos nada: sería ruido, la moneda
     * ya está en la columna Moneda de la línea.
     */
    _renderCurrencyIfConverted() {
        const conversion = this._conversionForThisAccount();
        if (!conversion) {
            return null;
        }

        return <div className="ad-account-currency" key="ad-account-currency">
            {this.props.accountCurrency}
            <CurrencyConversionInfo conversions={[conversion]}/>
        </div>;
    }

    _conversionForThisAccount() {
        const accountCurrency = this.props.accountCurrency;
        const currencyConversions = this.props.currencyConversions;
        if (!accountCurrency || !Array.isArray(currencyConversions)) {
            return null;
        }
        return currencyConversions.find(conversion => conversion.from === accountCurrency) || null;
    }

    /**
     * Reloj ámbar de "datos no actualizados". Va acá y no `sync-alt` a propósito: en el tablero
     * ese ícono es un botón de acción (sincronizar), no un estado.
     */
    _renderDataStaleIconIfNecessary() {
        if (this._cellState() !== STATE_DATA_STALE)
            return null;

        return <span className="bu-cell-icon data-stale" key="ad-account-data-stale">
            <FaIcon iconName="clock"/>
        </span>;
    }

    _getNameWithAlert() {
        const classIfAlert = this._hasAlert() ? "bu-ad-account-name-with-alert" : "";

        return <span className={classIfAlert} style={{verticalAlign: "middle", paddingLeft:".3em"}}>{this.props.accountName} [{this.props.accountId}]</span>
    }

    _getPlatformLogo() {
        let platformLogo = null;
        const style = {
            verticalAlign: 'middle',
            margin: '0'
        };

        if (this.props.channel === 'GOOGLE') {
            platformLogo = <GoogleLogo height={"13"} width={"13"} styles={style} />
        } else if (this.props.channel === 'FACEBOOK') {
            platformLogo = <FacebookLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === 'SHEET') {
            platformLogo = <SheetLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === 'TIKTOK') {
            platformLogo = <TiktokLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === 'LINKEDIN') {
            platformLogo = <LinkedinLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === SHOPIFY_CHANNEL) {
            platformLogo = <ShopifyLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === TIENDA_NUBE_CHANNEL) {
            platformLogo = <TiendaNubeLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === WOOCOMMERCE_CHANNEL) {
            platformLogo = <WooCommerceLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === VTEX_CHANNEL) {
            platformLogo = <VtexLogo height={"16"} width={"16"} styles={style}/>
        } else if (this.props.channel === ANALYTICS_CHANNEL) {
            platformLogo = <AnalyticsLogo height={"14"} width={"14"} styles={style}/>
        } else if (this.props.channel === DV360_CHANNEL) {
            platformLogo = <DV360Logo height={16} width={16} hspace={0} vspace={0} styles={style}/>
        } else if (this.props.channel === MERCADO_ADS_CHANNEL) {
            platformLogo = <MercadoAdsLogo height={16} width={16} hspace={0} vspace={0} styles={style}/>
        }
        return platformLogo;
    }
}

export const BusinessUnitAdAccountNameCell = withTranslation()(BusinessUnitAdAccountNameCellClass);
export {BusinessUnitAdAccountNameCellClass};
export default BusinessUnitAdAccountNameCell;
